// Nouvelle attribution d'emplacement (droit créer du domaine marche_municipal) : emplacement
// disponible (préchoisi depuis le plan), période et périodicité, commerçant saisi au guichet
// (tiers ou compte du portail retrouvé par son identifiant) ou dossier de demande accordé ; la
// redevance de la première période est affichée avant l'acte avec la règle du module des recettes
// (R7, au franc près) ; un chevauchement est refusé par le serveur et expliqué (409).
import { CODES_PERIODICITES, decouperPeriodes, schemaCreationAttribution } from '@cc/contrats';
import { Bandeau, Bouton, Champ, DateChamp, Formulaire, GrilleChamps, LienBouton, Panneau, Selection, ZoneTexte, type ErreurChamp } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { requete } from '../../api/client';
import { ErreurApi } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { AffichageErreur } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { creerAttribution, INVALIDER_MARCHES, useEmplacement, useMarche, useMarches, useTarification } from './api';
import { expliquerRefusAttribution, LIBELLES_PERIODICITE, montant, periodeParDefaut } from './logique';
import type { CreationAttribution, PeriodiciteRedevance } from './types';
import styles from './marches.module.css';

interface UsagerRecherche {
  id: string;
  identifiant: string;
  nom: string;
  prenoms: string | null;
}

/** Redevance de la première période, calculée par le module des recettes avant l'acte. */
function ApercuRedevance({ emplacementId, periode, periodicite }: { emplacementId: string | null; periode: { debut: string; fin: string }; periodicite: PeriodiciteRedevance }) {
  const premiere = useMemo(() => decouperPeriodes(periode.debut, periode.fin, periodicite)[0] ?? null, [periode, periodicite]);
  const t = useTarification(emplacementId, premiere ? { debut: premiere.debut, fin: premiere.fin } : null);
  if (!emplacementId || !premiere) return null;
  return (
    <Panneau titre="Redevance de la première période" niveau={3} interne>
      {t.isPending ? <p className={styles.aide}>Calcul par le module des recettes…</p> : null}
      {t.error instanceof ErreurApi && t.error.statut < 500 ? <Bandeau type="attention">{expliquerRefusAttribution(t.error.code, t.error.message, t.error.details)}</Bandeau> : t.error ? <AffichageErreur erreur={t.error} fonction="La tarification" /> : null}
      {t.data ? (
        <dl className={styles.definitions}>
          <dt>Période</dt>
          <dd>
            du {date(premiere.debut)} au {date(premiere.fin)}
          </dd>
          <dt>Montant</dt>
          <dd className={styles.montant} data-testid="montant-apercu">
            {montant(t.data.montant, t.data.fictif)}
          </dd>
          <dt>Règle</dt>
          <dd className={styles.regle}>{t.data.regle}</dd>
        </dl>
      ) : null}
      <p className={styles.aide}>Cette créance est émise dans la transaction de l&apos;attribution ; les périodes suivantes le sont chaque début de période par la tâche quotidienne.</p>
    </Panneau>
  );
}

export function PageNouvelleAttribution() {
  const naviguer = useNavigate();
  const [params] = useSearchParams();
  const [marcheId, setMarcheId] = useState('');
  const [emplacementId, setEmplacementId] = useState(params.get('emplacement') ?? '');
  const prechoisi = useEmplacement(params.get('emplacement'));
  useEffect(() => {
    if (prechoisi.data && !marcheId) setMarcheId(prechoisi.data.marche_id);
  }, [prechoisi.data, marcheId]);
  const marches = useMarches({ actif: 'oui' });
  const marche = useMarche(marcheId || null);
  const cielOuvert = marche.data?.mode === 'ciel_ouvert';
  const periodiciteDefaut = cielOuvert ? 'mensuelle' : (marche.data?.periodicite_defaut as PeriodiciteRedevance | undefined) ?? 'mensuelle';
  const [periodicite, setPeriodicite] = useState<PeriodiciteRedevance | ''>('');
  const [periode, setPeriodeBrute] = useState(() => periodeParDefaut('mensuelle'));
  const [periodeTouchee, setPeriodeTouchee] = useState(false);
  const setPeriode = (f: (p: { debut: string; fin: string }) => { debut: string; fin: string }) => {
    setPeriodeTouchee(true);
    setPeriodeBrute(f);
  };
  // Tant que l'agent n'a pas saisi de période, elle suit la périodicité du marché (une période de redevance à compter d'aujourd'hui).
  useEffect(() => {
    if (!periodeTouchee) setPeriodeBrute(periodeParDefaut(periodiciteDefaut));
  }, [periodiciteDefaut, periodeTouchee]);
  const [source, setSource] = useState<'guichet' | 'dossier'>('guichet');
  const [commercant, setCommercant] = useState({ nom: '', type: 'personne', telephone: '', identifiant: '' });
  const [compte, setCompte] = useState<UsagerRecherche | null>(null);
  const [erreurCompte, setErreurCompte] = useState<string | null>(null);
  const [dossierId, setDossierId] = useState('');
  const [divers, setDivers] = useState({ activite: '', decision_reference: '', observations: '' });
  const [erreurs, setErreurs] = useState<ErreurChamp[]>([]);
  const periodiciteEffective = cielOuvert ? 'mensuelle' : periodicite || periodiciteDefaut;
  const disponibles = (marche.data?.emplacements ?? []).filter((e) => e.etat === 'disponible' || e.id === emplacementId);

  const rechercherCompte = async () => {
    setErreurCompte(null);
    setCompte(null);
    try {
      const u = await requete<UsagerRecherche>('/usagers/recherche', { parametres: { identifiant: commercant.identifiant.trim() } });
      setCompte(u);
      if (!commercant.nom) setCommercant((c) => ({ ...c, nom: [u.prenoms, u.nom].filter(Boolean).join(' ') }));
    } catch (e) {
      setErreurCompte(e instanceof ErreurApi && e.statut === 404 ? 'Aucun compte du portail ne porte cet identifiant.' : e instanceof Error ? e.message : 'Recherche impossible');
    }
  };

  const action = useAction({
    executer: () => {
      const brut: CreationAttribution = {
        emplacement_id: emplacementId,
        periode,
        periodicite: periodiciteEffective,
        activite: divers.activite || undefined,
        decision_reference: divers.decision_reference || undefined,
        observations: divers.observations || undefined,
        ...(source === 'dossier'
          ? { dossier_id: dossierId.trim() }
          : { commercant: { usager_compte_id: compte?.id, nom: commercant.nom || undefined, type: commercant.type as 'personne' | 'entreprise', telephone: commercant.telephone || undefined } }),
      };
      const v = schemaCreationAttribution.safeParse(brut);
      if (!v.success) {
        setErreurs(v.error.issues.map((i) => ({ champ: String(i.path[0] ?? 'formulaire'), message: `${i.path.join('.')} : ${i.message}` })));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs([]);
      return creerAttribution(v.data);
    },
    succes: (a) => `Attribution ${a.numero} enregistrée${a.redevances.length ? `, redevance ${montant(a.redevances[0]?.creance.montant_du)} émise` : ''}.`,
    invalider: INVALIDER_MARCHES,
    apres: (a) => void naviguer(`/marches/attributions/${a.id}`),
  });
  const refus = action.erreur instanceof ErreurApi ? action.erreur : null;

  return (
    <>
      <EnTetePage
        etiquette="Marchés municipaux"
        titre="Nouvelle attribution"
        description="Toute période qui chevauche une attribution en vigueur sur le même emplacement est refusée par le serveur ; la redevance de la première période est émise avec l'acte."
        actions={
          <LienBouton href={emplacementId && prechoisi.data ? `/marches/${prechoisi.data.marche_id}?emplacement=${emplacementId}` : '/marches/attributions'} icone={ArrowLeft}>
            Retour
          </LienBouton>
        }
      />
      <div className={styles.disposition}>
        <Formulaire libelle="Nouvelle attribution" onSoumettre={() => action.lancer(undefined)} erreurs={erreurs} actions={
          <>
            <Bouton type="submit" variante="principal" chargement={action.enCours} disabled={!emplacementId}>
              Attribuer et émettre la redevance
            </Bouton>
            <LienBouton href="/marches/attributions" variante="secondaire">
              Annuler
            </LienBouton>
          </>
        }>
          <Panneau titre="Emplacement" niveau={3} interne>
            <GrilleChamps>
              <Selection libelle="Marché" optionVide="Choisir" options={(marches.data ?? []).map((m) => ({ valeur: m.id, libelle: m.nom }))} value={marcheId} onChange={(e) => { setMarcheId(e.target.value); setEmplacementId(''); }} required />
              <Selection
                libelle="Emplacement disponible"
                optionVide={marcheId ? 'Choisir' : 'Choisir d\'abord un marché'}
                options={disponibles.map((e) => ({ valeur: e.id, libelle: `${e.reference} · ${e.type_libelle}, ${e.surface_m2.toLocaleString('fr-FR')} m² (${e.zone.id ? `zone ${e.zone.code ?? '?'}` : 'sans zone'})` }))}
                value={emplacementId}
                onChange={(e) => setEmplacementId(e.target.value)}
                required
                id="emplacement_id"
              />
            </GrilleChamps>
          </Panneau>
          <Panneau titre="Période et redevance" niveau={3} interne>
            <GrilleChamps>
              <DateChamp libelle="Du" id="periode" value={periode.debut} onChange={(e) => setPeriode((p) => ({ ...p, debut: e.target.value }))} required />
              <DateChamp libelle="Au" value={periode.fin} onChange={(e) => setPeriode((p) => ({ ...p, fin: e.target.value }))} aide="Trois ans au plus, renouvelable" required />
              <Selection libelle="Périodicité de la redevance" optionVide={`Celle du marché (${LIBELLES_PERIODICITE[periodiciteDefaut].toLowerCase()})`} options={CODES_PERIODICITES.filter((p) => !cielOuvert || p === 'mensuelle').map((p) => ({ valeur: p, libelle: LIBELLES_PERIODICITE[p] }))} value={cielOuvert ? 'mensuelle' : periodicite} onChange={(e) => setPeriodicite(e.target.value as PeriodiciteRedevance | '')} />
            </GrilleChamps>
          </Panneau>
          <Panneau titre="Commerçant" niveau={3} interne>
            <div role="radiogroup" aria-label="Origine de l'attribution" className={styles.puces}>
              <button type="button" role="radio" aria-checked={source === 'guichet'} className={styles.puce} onClick={() => setSource('guichet')}>Saisie au guichet</button>
              <button type="button" role="radio" aria-checked={source === 'dossier'} className={styles.puce} onClick={() => setSource('dossier')}>Dossier de demande accordé</button>
            </div>
            {source === 'dossier' ? (
              <Champ libelle="Identifiant du dossier accordé" id="dossier_id" value={dossierId} onChange={(e) => setDossierId(e.target.value)} aide="Dossier d'une démarche du domaine des marchés décidé favorablement par le maire ; le commerçant en est le demandeur." required />
            ) : (
              <GrilleChamps>
                <Champ libelle="Identifiant du compte portail (téléphone ou courriel)" value={commercant.identifiant} onChange={(e) => setCommercant((c) => ({ ...c, identifiant: e.target.value }))} facultatif aide={compte ? `Compte trouvé : ${[compte.prenoms, compte.nom].filter(Boolean).join(' ')}` : erreurCompte ?? undefined} />
                <Bouton variante="secondaire" onClick={() => void rechercherCompte()} disabled={!commercant.identifiant.trim()}>
                  Retrouver le compte
                </Bouton>
                <Champ libelle="Nom du commerçant" id="commercant" value={commercant.nom} onChange={(e) => setCommercant((c) => ({ ...c, nom: e.target.value }))} required={!compte} facultatif={Boolean(compte)} />
                <Selection libelle="Type" options={[{ valeur: 'personne', libelle: 'Personne' }, { valeur: 'entreprise', libelle: 'Entreprise' }]} value={commercant.type} onChange={(e) => setCommercant((c) => ({ ...c, type: e.target.value }))} />
                <Champ libelle="Téléphone" value={commercant.telephone} onChange={(e) => setCommercant((c) => ({ ...c, telephone: e.target.value }))} facultatif />
              </GrilleChamps>
            )}
            <GrilleChamps>
              <Champ libelle="Activité" value={divers.activite} onChange={(e) => setDivers((d) => ({ ...d, activite: e.target.value }))} facultatif />
              <Champ libelle="Référence de la décision" value={divers.decision_reference} onChange={(e) => setDivers((d) => ({ ...d, decision_reference: e.target.value }))} facultatif />
            </GrilleChamps>
            <ZoneTexte libelle="Observations" value={divers.observations} onChange={(e) => setDivers((d) => ({ ...d, observations: e.target.value }))} facultatif />
          </Panneau>
          {refus ? <Bandeau type="erreur" titre={refus.code === 'attribution_chevauchante' ? 'Chevauchement refusé' : 'Attribution refusée'}>{expliquerRefusAttribution(refus.code, refus.message, refus.details)}</Bandeau> : null}
        </Formulaire>
        <aside className={styles.cote} aria-label="Redevance">
          <ApercuRedevance emplacementId={emplacementId || null} periode={periode} periodicite={periodiciteEffective} />
        </aside>
      </div>
    </>
  );
}
